import { useEffect } from 'react'

import { Consultation } from '@/core/models/consultation.model'
import { Room } from '@/core/models/room.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  consultationUpdatedSocketEvent,
  getRpiMeetingId,
  joinConsultationSocketEvent,
  leaveConsultationSocketEvent,
} from '@/redux/reducers/consultation.reducer'
import { addUserToRoom, getRoom } from '@/redux/reducers/room.reducer'

type UseLiveConsultationLifecycleParams = {
  consultation?: Consultation
  consultationId?: string
  room?: Room
  isStreamer: boolean
  paymentMenuSearchParam: string | null
}

export function useConsultationLifecycle({
  consultation,
  consultationId,
  room,
  isStreamer,
  paymentMenuSearchParam,
}: UseLiveConsultationLifecycleParams) {
  const dispatch = useAppDispatch()
  const authUser = useAppSelector(selectAuthUser)

  // Fetch room
  useEffect(() => {
    if (!consultationId) return
    dispatch(getRoom(consultationId))
  }, [dispatch, consultationId])

  // Get RPI meeting ID if user is streamer
  useEffect(() => {
    if (isStreamer) {
      dispatch(getRpiMeetingId())
    }
  }, [dispatch, isStreamer])

  // Add user to room and join consultation socket
  useEffect(() => {
    if (!consultationId) return

    dispatch(joinConsultationSocketEvent(consultationId))

    return () => {
      dispatch(leaveConsultationSocketEvent(consultationId))
    }
  }, [dispatch, consultationId])

  // Add user to room if not creator
  useEffect(() => {
    if (consultationId && room && authUser?._id !== (typeof room.creator === 'string' ? room.creator : room.creator._id)) {
      dispatch(addUserToRoom(consultationId))
    }
  }, [authUser, consultationId, dispatch, room])

  // Handle payment menu search param
  useEffect(() => {
    if (consultation && paymentMenuSearchParam === 'payment') {
      dispatch(consultationUpdatedSocketEvent(consultation))
    }
  }, [consultation, dispatch, paymentMenuSearchParam])
}
